import {
  ConfigurationFieldOwner,
  ConfigurationFieldSpecificationResponse,
  ConfigurationValueKind,
} from '@ydistri/api-sdk';
import { useCallback } from 'react';
import { apiSlice } from './api';
import { ConfigurationFieldSpecificationsCollection } from '../swagger/collections';

/**
 * The bounds the server's value validators apply, for the inputs to clamp and hint with the same
 * numbers. Static per build, so one fetch per session is enough.
 */
export const apiConfigurationFieldSpecifications = apiSlice.injectEndpoints({
  endpoints: builder => ({
    // eslint-disable-next-line @typescript-eslint/no-invalid-void-type -- no argument
    getConfigurationFieldSpecifications: builder.query<
      ConfigurationFieldSpecificationResponse[],
      void
    >({
      queryFn: async () => {
        const { data } =
          await ConfigurationFieldSpecificationsCollection.getConfigurationFieldSpecifications();
        return { data: data.data };
      },
      keepUnusedDataFor: 24 * 60 * 60,
    }),
  }),
});

export const { useGetConfigurationFieldSpecificationsQuery } = apiConfigurationFieldSpecifications;

export interface InputBounds {
  min?: number;
  max?: number;
  allowDecimals: boolean;
  /** How many places a decimal field keeps; undefined when the server does not limit them. */
  decimalPlaces?: number;
}

/** What an input needs from one specification row; a field with no row is unbounded. */
export const boundsOf = (
  specifications: ConfigurationFieldSpecificationResponse[] | undefined,
  owner: ConfigurationFieldOwner,
  key: string,
): InputBounds => {
  const row = specifications?.find(s => s.owner === owner && s.key === key);
  return {
    min: row?.minimum ?? undefined,
    max: row?.maximum ?? undefined,
    allowDecimals: row?.kind === ConfigurationValueKind.Decimal,
    decimalPlaces: row?.decimalPlaces ?? undefined,
  };
};

export interface FieldBounds extends InputBounds {
  /** The specification has answered; until then an input has no bounds to hold to. */
  ready: boolean;
  failed: boolean;
  retry: () => void;
}

/** A field's bounds and whether they are known - an unbounded field and one not loaded yet differ. */
export const useFieldBounds = (owner: ConfigurationFieldOwner, key: string): FieldBounds => {
  const { data, isSuccess, isError, refetch } = useGetConfigurationFieldSpecificationsQuery();
  const retry = useCallback(() => {
    void refetch();
  }, [refetch]);
  return { ...boundsOf(data, owner, key), ready: isSuccess, failed: isError, retry };
};
